import { EditorProps, Monaco } from '@monaco-editor/react';
import React from 'react';

const useJsonEditor = ({
  id,
  code,
  setCode,
}: {
  id: string;
  code: string;
  setCode: (val: string) => void;
}) => {
  const [isLoading, setIsLoading] = React.useState(true);
  const [copied, setCopied] = React.useState(false);
  const [error, setError] = React.useState('');
  const editorRef = React.useRef<any>(null);
  const fileInputRef = React.useRef(null);

  const editorOptions: EditorProps['options'] = {
    automaticLayout: true,
    formatOnPaste: true,
    formatOnType: true,
    minimap: { enabled: true },
    scrollBeyondLastLine: false,
    fontSize: 14,
    tabSize: 2,
    insertSpaces: true,
    wordWrap: 'on',
    bracketPairColorization: { enabled: true },
    folding: true,
    lineNumbers: 'on',
    renderWhitespace: 'selection',
  };

  const validateJson = (jsonString: string) => {
    try {
      JSON.parse(jsonString);
      setError('');
      return true;
    } catch (err) {
      setError(`Invalid JSON: ${(err as Error).message}`);
      return false;
    }
  };

  const handleEditorDidMount = (editor: any, monaco: Monaco) => {
    editorRef.current = editor;
    setIsLoading(false);

    // Configure JSON language features
    monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
      validate: true,
      allowComments: true,
      schemas: [],
      enableSchemaRequest: false,
    });

    // Add custom keybinding for formatting
    editor.addCommand(
      monaco.KeyMod.CtrlCmd | monaco.KeyMod.Shift | monaco.KeyCode.KeyI,
      () => {
        formatJson();
      },
    );

    // Validate initial content
    validateJson(code);
  };

  const handleEditorChange = (value?: string) => {
    setCode(value || '');
    validateJson(value || '');
  };

  const formatJson = () => {
    if (editorRef.current) {
      try {
        // Use Monaco's built-in formatting action
        editorRef.current.getAction('editor.action.formatDocument').run();
      } catch (err) {
        // Fallback manual formatting
        try {
          const parsed = JSON.parse(code);
          const formatted = JSON.stringify(parsed, null, 2);
          editorRef.current.setValue(formatted);
          setError('');
        } catch (parseErr) {
          setError(`Cannot format: ${(parseErr as Error).message}`);
        }
      }
    }
  };

  const minifyJson = () => {
    try {
      const parsed = JSON.parse(code);
      const minified = JSON.stringify(parsed);
      editorRef.current.setValue(minified);
      setCode(minified);
      setError('');
    } catch (err: any) {
      setError(`Cannot minify: ${err.message}`);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const exportJson = () => {
    const blob = new Blob([code], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${id}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const uploadFile = (event: React.InputEvent<HTMLInputElement>) => {
    const element = event.target as HTMLInputElement;
    if (!element.files || element.files.length === 0) return;
    const file = element.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result;
        if (editorRef.current && typeof content === 'string') {
          editorRef.current.setValue(content);
          setCode(content);
        }
      };
      reader.readAsText(file);
    }
  };

  return {
    code,
    error,
    copied,
    isLoading,
    fileInputRef,
    editorOptions,
    minifyJson,
    formatJson,
    exportJson,
    uploadFile,
    validateJson,
    copyToClipboard,
    handleEditorChange,
    handleEditorDidMount,
  };
};

export { useJsonEditor };
